import clsx from "clsx";
import { FC, RefObject, useEffect, useRef } from "react";

import { ErrorPanel } from "@tsmono/react/components";
import {
  useLatestRef,
  useStatefulScrollPosition,
  useVisitId,
} from "@tsmono/react/hooks";

import { kSampleMessagesTabId, kSampleTranscriptTabId } from "../../constants";
import { useSelectedEvalSampleData } from "../../state/hooks";
import { useStore } from "../../state/store";
import {
  useCurrentLogFile,
  useCurrentSampleHandle,
} from "../routing/currentSelection";
import { useSampleDetailNavigation } from "../routing/sampleNavigation";

import styles from "./InlineSampleDisplay.module.css";
import { SampleDisplay } from "./SampleDisplay";

interface InlineSampleDisplayProps {
  className?: string | string[];
  /** Optional ref that receives the inner scroller element so callers can
   *  hook scroll listeners on the actual scrolling viewport. */
  scrollRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Inline Sample Display
 */
export const InlineSampleDisplay: FC<InlineSampleDisplayProps> = ({
  className,
  scrollRef: externalScrollRef,
}) => {
  const sampleData = useSelectedEvalSampleData();
  const showActivity =
    sampleData.status === "loading" || sampleData.status === "streaming";

  const localScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef ?? localScrollRef;
  const sampleTab = useStore((state) => state.app.tabs.sample);
  const sampleDetailNavigation = useSampleDetailNavigation();
  // The Transcript/Messages tabs each mount a VirtualList with its own
  // snapshot-based restore on this same scroller; this hook must stand down
  // there so only one system ever writes scrollTop for those tabs.
  const isVirtualizedTab =
    sampleTab === kSampleTranscriptTabId || sampleTab === kSampleMessagesTabId;
  // Key by VISIT + tab: the saved position belongs to one visit to one
  // sample's tab. Tab flips within the visit share the key (flipping back
  // restores), but a later RETURN to the same sample is a new visit and must
  // land at the top — identity-based keys would restore across visits.
  const logFile = useCurrentLogFile();
  const sampleHandle = useCurrentSampleHandle();
  const visitId = useVisitId(
    `${logFile}-${sampleHandle?.id}-${sampleHandle?.epoch}`
  );
  useStatefulScrollPosition(
    scrollRef,
    `inline-sample-scroller-${visitId}-${sampleTab}`,
    1000,
    !isVirtualizedTab
  );

  // A new visit starts at the top: the scroller survives sample hops (same
  // route element), so the previous sample's scrollTop would otherwise carry
  // over whenever the content swap never passes through an empty commit.
  // Deep-link mounts stand down — the transcript's landing owns the position
  // (read via a ref so a later ?event= cleanup can't re-trigger the reset).
  const mountsAtDeepLink = !!(
    sampleDetailNavigation.event || sampleDetailNavigation.message
  );
  const deepLinkRef = useLatestRef(mountsAtDeepLink);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Stand down for virtualized tabs: VirtualList owns scrollTop there (its
    // own no-snapshot branch lands non-following fresh mounts at top), so an
    // uncoordinated reset here fights its follow-tail on a running sample.
    if (!deepLinkRef.current && !isVirtualizedTab) {
      scrollRef.current?.scrollTo({ top: 0 });
    }
  }, [visitId, scrollRef, isVirtualizedTab, deepLinkRef]);

  return (
    <div className={clsx(className, styles.container)}>
      <div className={clsx(styles.scroller)} ref={scrollRef}>
        {/* The transcript provides its own StickyScrollProvider (with the
            chrome offset) around its event panels; no container-level
            provider is needed here. */}
        <div>
          {sampleData.error ? (
            <ErrorPanel
              title="Unable to load sample"
              error={sampleData.error}
            />
          ) : (
            <SampleDisplay
              id={"inline-sample-display"}
              showActivity={showActivity}
              scrollRef={scrollRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};
